import { useMemo, useState } from 'react';
import { History, RotateCcw } from 'lucide-react';
import type { FloorPlanVersionSummary, SaveFloorPlanObjectsResult } from '@peles/shared';
import { EntityModal } from '../common/EntityModal';
import { EntityList, StatusBadge, type EntityListColumn } from '../common/EntityList';
import { useDialog } from '../../context/DialogContext';
import { useFloorPlanVersions, useRestoreFloorPlanVersion } from '../../hooks/useFloorPlans';
import { saveConflictOf } from '../../api/floor-plans';
import { formatSavedAt } from './saveStatus';

const PAGE_SIZE = 10;

const KIND_LABEL: Record<FloorPlanVersionSummary['kind'], { label: string; color: string }> = {
  MANUAL: { label: 'Saved', color: 'green' },
  AUTO: { label: 'Autosave', color: 'gray' },
  RESTORE: { label: 'Before restore', color: 'purple' },
  REPLACED: { label: 'Replaced', color: 'orange' },
};

interface FloorPlanHistoryModalProps {
  open: boolean;
  onClose: () => void;
  floorPlanId: string;
  /** The version the canvas is based on — a restore is refused if it moved on. */
  baseVersion?: number;
  /** Whether the canvas holds edits a restore would discard. */
  hasUnsavedChanges: boolean;
  onRestored: (result: SaveFloorPlanObjectsResult) => void;
}

/**
 * The saved copies of a plan, newest first, each restorable. Restoring keeps
 * the current state as a copy of its own first, so it can be undone the same
 * way.
 */
export function FloorPlanHistoryModal({
  open,
  onClose,
  floorPlanId,
  baseVersion,
  hasUnsavedChanges,
  onRestored,
}: FloorPlanHistoryModalProps) {
  const { confirm, alert } = useDialog();
  const [page, setPage] = useState(1);
  const { data: versions = [], isLoading } = useFloorPlanVersions(floorPlanId, open);
  const restore = useRestoreFloorPlanVersion();

  const pageItems = useMemo(
    () => versions.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE),
    [versions, page],
  );

  const handleRestore = async (v: FloorPlanVersionSummary) => {
    const ok = await confirm({
      title: 'Restore this version?',
      message: hasUnsavedChanges
        ? `The plan goes back to ${describe(v)}. Your unsaved changes will be discarded; the saved plan is kept as a version you can return to.`
        : `The plan goes back to ${describe(v)}. The current plan is kept as a version you can return to.`,
      confirmLabel: 'Restore',
      variant: 'warning',
    });
    if (!ok) return;
    try {
      const result = await restore.mutateAsync({
        floorPlanId,
        versionId: v.id,
        expectedVersion: baseVersion,
      });
      onRestored(result);
    } catch (err) {
      const conflict = saveConflictOf(err);
      await alert({
        title: 'Not restored',
        message: conflict
          ? `${conflict.message} Load their version first, then restore.`
          : 'The version could not be restored. Nothing was changed.',
        variant: 'error',
      });
    }
  };

  const columns: EntityListColumn<FloorPlanVersionSummary>[] = [
    {
      key: 'when',
      header: 'When',
      render: (v) => <span className="whitespace-nowrap">{formatSavedAt(v.createdAt)}</span>,
    },
    {
      key: 'kind',
      header: 'Type',
      render: (v) => (
        <StatusBadge label={KIND_LABEL[v.kind].label} color={KIND_LABEL[v.kind].color} />
      ),
    },
    {
      key: 'label',
      header: 'Note',
      render: (v) => v.label ?? <span className="text-gray-400">—</span>,
      hideOnMobile: true,
    },
    { key: 'by', header: 'By', render: (v) => v.createdBy?.name ?? '—', hideOnMobile: true },
    {
      key: 'objects',
      header: 'Objects',
      align: 'right',
      render: (v) => v.objectCount,
      hideOnMobile: true,
    },
    {
      key: 'restore',
      header: '',
      align: 'right',
      render: (v) => (
        <RestoreButton version={v} disabled={restore.isPending} onClick={handleRestore} />
      ),
    },
  ];

  return (
    <EntityModal open={open} onClose={onClose} title="Version history" size="lg">
      <p className="mb-3 text-sm text-gray-600">
        A copy is kept on every Save, every 10 minutes while autosaving, before each restore, and
        before a save replaces someone else's work.
      </p>
      <EntityList
        items={pageItems}
        columns={columns}
        getKey={(v) => v.id}
        isLoading={isLoading}
        page={page}
        pageSize={PAGE_SIZE}
        total={versions.length}
        onPageChange={setPage}
        emptyIcon={<History size={32} />}
        emptyMessage="No versions yet — one is kept the next time you save."
        renderCard={(v) => (
          <div className="flex items-center justify-between gap-3">
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <StatusBadge label={KIND_LABEL[v.kind].label} color={KIND_LABEL[v.kind].color} />
                <span className="text-sm text-gray-900">{formatSavedAt(v.createdAt)}</span>
              </div>
              <p className="mt-1 truncate text-xs text-gray-500">
                {[v.label, v.createdBy?.name, `${v.objectCount} objects`]
                  .filter(Boolean)
                  .join(' · ')}
              </p>
            </div>
            <RestoreButton version={v} disabled={restore.isPending} onClick={handleRestore} />
          </div>
        )}
      />
    </EntityModal>
  );
}

function describe(v: FloorPlanVersionSummary): string {
  return v.label ? `"${v.label}" (${formatSavedAt(v.createdAt)})` : formatSavedAt(v.createdAt);
}

function RestoreButton({
  version,
  disabled,
  onClick,
}: {
  version: FloorPlanVersionSummary;
  disabled: boolean;
  onClick: (v: FloorPlanVersionSummary) => void;
}) {
  return (
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation();
        onClick(version);
      }}
      disabled={disabled}
      className="inline-flex items-center gap-1 rounded-md border border-gray-300 px-2.5 py-1 text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
      data-testid={`restore-version-${version.id}`}
    >
      <RotateCcw size={12} aria-hidden="true" />
      Restore
    </button>
  );
}
